import { capSQLiteSet } from "@capacitor-community/sqlite";
import center from "@turf/center";
import { Feature } from "geojson";
import { READY_SYNC_STATES } from "../../../record-associate-sync-states";
import { parseSyncError, serialiseRecordSyncErrorForStorage } from "../../../services/sync-error.service";
import { CoreoRecord, CoreoRecordSyncStatus } from "../../../types";
import AppDatabase from "../app-db.service";
import { SyncRecord } from "../app-sync.service";
import SQLDatabase, { CURSOR_RESULT_KEY, SQLQueryBuilder } from "../sql.service";
import { recordHasValidationIssueCondition, rootRecordOnlyNotExistsClause } from "../validation-issues.sql";
import { AppDatabaseRepo } from "./base.db";

export interface AppDatabaseRecord {
  id: number;
  createdAt: string;
  updatedAt: string;
  timestamp: number;
  projectId: number;
  surveyId: number;
  userId: number;
  deletedAt: string;
  syncState: CoreoRecordSyncStatus;
  origSyncState: CoreoRecordSyncStatus;
  syncError: string | null;
  treeSyncError: number | null;
  geometryCenterLat: number;
  geometryCenterLng: number;
  geometry: any;
  state: number;
}

export class AppDatabaseRecordRepo extends AppDatabaseRepo<AppDatabaseRecord, SyncRecord, CoreoRecord> {

  tableName = 'records';
  columns = [
    ['id', 'INTEGER NOT NULL'],
    ['projectId', 'INTEGER NOT NULL'],
    ['createdAt', 'TEXT'],
    ['updatedAt', 'TEXT'],
    ['timestamp', 'INTEGER NOT NULL'],
    ['deletedAt', 'TEXT'],
    ['syncState', 'INTEGER NOT NULL'],
    ['syncError', 'TEXT'],
    ['treeSyncError', 'INTEGER'],
    ['surveyId', 'INTEGER NOT NULL'],
    ['userId', 'INTEGER NOT NULL'],
    ['state', 'INTEGER NOT NULL'],
    ['geometry', 'TEXT'],
    ['geometryCenterLng', 'REAL'],
    ['geometryCenterLat', 'REAL'],
    ['origSyncState', 'INTEGER']
  ];
  primaryKey = ['id'];
  references = [];
  indexes = [
    'CREATE INDEX IF NOT EXISTS idx_records_projectId_surveyId ON records(projectId, surveyId)',
    'CREATE INDEX IF NOT EXISTS idx_records_projectId_syncState ON records(projectId, syncState)',
    'CREATE INDEX IF NOT EXISTS idx_records_projectId_surveyId_id_geometry ON records(projectId, surveyId, id DESC) WHERE geometry IS NOT NULL',
    'CREATE INDEX IF NOT EXISTS idx_records_projectId_syncState_timestamp ON records(projectId, syncState, timestamp DESC, id DESC)',
    'CREATE INDEX IF NOT EXISTS idx_records_projectId_surveyId_timestamp ON records(projectId, surveyId, timestamp DESC)',
    'CREATE INDEX IF NOT EXISTS idx_records_projectId_treeSyncError ON records(projectId, treeSyncError)'
  ];

  public toInsertSet(record: CoreoRecord): capSQLiteSet[] {
    const c = record.geometry && center(record.geometry as any);
    // TODO cache this
    const dataColumns = AppDatabase.instance.formDataColumnMap.get(record.surveyId);
    const data = dataColumns.map(path => {
      const p = record.data?.[path];
      if (typeof p === 'undefined') {
        return null;
      }

      return Array.isArray(p) ? JSON.stringify(p) : p;
    });
    const columnsList = ["id", ...dataColumns].map(c => `"${c}"`).join(',');
    const valuesList = ["id", ...dataColumns].map(() => '?').join(',');

    return [{
      statement: `INSERT OR REPLACE INTO records ("id","projectId","createdAt","updatedAt","timestamp","deletedAt","syncState","syncError","treeSyncError","surveyId","userId","state","geometry","geometryCenterLng","geometryCenterLat") VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
      values: [
        record.id,
        record.projectId,
        record.createdAt,
        record.updatedAt,
        record.timestamp,
        record.deletedAt,
        record.syncState ?? CoreoRecordSyncStatus.SUBMITTED,
        serialiseRecordSyncErrorForStorage(record.syncError),
        record.treeSyncError ?? null,
        record.surveyId ?? 0,
        record.userId,
        record.state,
        record.geometry ? JSON.stringify(record.geometry) : null,
        c?.geometry?.coordinates?.[0] ?? null,
        c?.geometry?.coordinates?.[1] ?? null,
      ]
    }, {
      statement: `INSERT OR REPLACE INTO records_${record.surveyId} (${columnsList}) VALUES (${valuesList})`,
      values: [
        record.id,
        ...data
      ]
    }];
  }

  deserialiseCore(record: AppDatabaseRecord): CoreoRecord {
    const { id, createdAt, projectId, updatedAt, deletedAt, surveyId, userId, syncState, syncError, treeSyncError, geometry, geometryCenterLng, geometryCenterLat, state, timestamp, origSyncState } = record;
    if (!surveyId) {
      console.warn('MISSING ', record);
    }

    return {
      id,
      createdAt,
      updatedAt,
      deletedAt,
      surveyId,
      formId: surveyId,
      associates: {},
      timestamp,
      attachments: [],
      projectId,
      syncState,
      syncError: parseSyncError(syncError),
      treeSyncError,
      origSyncState,
      state,
      userId,
      geometry: geometry && JSON.parse(geometry),
      data: {},
      geometryCenter: geometry && {
        type: 'Point',
        coordinates: [geometryCenterLng, geometryCenterLat]
      },
    };
  }

  async deserialise(record: AppDatabaseRecord): Promise<CoreoRecord> {
    const core = this.deserialiseCore(record);
    core.data = await this.getRecordData(record.id, record.surveyId);
    return core;
  }

  public async getRecordData(recordId: number, formId: number) {
    const query = SQLDatabase.select().from(`records_${formId}`)
      .where('id = ?', recordId);
    const result = await SQLDatabase.instance.query<any>(query);
    if (result.length === 0) {
      return {};
    }
    const { id, ...data } = result[0];
    return data;
  }

  private async findRecordsCoreByIds(ids: number[]): Promise<CoreoRecord[]> {
    if (ids.length === 0) {
      return [];
    }
    const rows = await SQLDatabase.instance.query<AppDatabaseRecord>(
      SQLDatabase.select().from('records').where('id IN ?', ids)
    );
    return rows.map((row) => this.deserialiseCore(row));
  }

  public async getRecordChildren(record: CoreoRecord, recursive: boolean = true): Promise<CoreoRecord[]> {

    // Children are defined by attributes on the record's survey with a questionType of "child"
    const query = SQLDatabase.select()
      .from('attributes')
      .field('id')
      .field('surveyId')
      .where('projectId = ?', record.projectId)
      .where('questionType = ?', 'child');

    const childAttributes = await SQLDatabase.instance.query<any>(query);

    const findChildren = async (record: CoreoRecord, output: CoreoRecord[] = []): Promise<CoreoRecord[]> => {
      // Find child attributes for this record
      const attributes = childAttributes.filter(a => a.surveyId === record.surveyId);

      if (attributes.length === 0) {
        return output;
      }

      const associationsQuery = SQLDatabase.select()
        .from('associations')
        .field('targetId')
        .where('sourceId = ?', record.id)
        .where('attributeId IN ?', attributes.map(a => a.id));

      const result = await SQLDatabase.instance.query<any>(associationsQuery);
      if (result.length === 0) {
        return output;
      }
      const ids = result.map(r => r.targetId);
      const childRecords = (await this.findRecordsCoreByIds(ids)).map(a => ({ ...a, parentId: record.id }));
      output.push(...childRecords);
      if (recursive) {
        for (const child of childRecords) {
          await findChildren(child, output);
        }
      }
      return output;
    }
    return findChildren(record);
  }

  public async getRecordChildrenForMap(record: CoreoRecord, recursive: boolean = true): Promise<CoreoRecord[]> {
    return this.getRecordChildren(record, recursive);
  }

  public async getRecordAssociations(record: CoreoRecord): Promise<CoreoRecord[]> {
    const query = SQLDatabase.select()
      .from('attributes')
      .field('id')
      .field('surveyId')
      .where('projectId = ?', record.projectId)
      .where('questionType = ?', 'association');
    const associationAttributes = await SQLDatabase.instance.query<any>(query);

    if (associationAttributes.length === 0) {
      return [];
    }

    const associationsQuery = SQLDatabase.select()
      .from('associations')
      .field('targetId')
      .field('sourceId')
      .where('sourceId = ? OR targetId = ?', record.id, record.id)
      .where('attributeId IN ?', associationAttributes.map(a => a.id));

    const result = await SQLDatabase.instance.query<any>(associationsQuery);
    if (result.length === 0) {
      return [];
    }

    const ids = result.map(r => r.targetId === record.id ? r.sourceId : r.targetId);
    return this.findRecordsCoreByIds(ids);
  }

  public async getRecordAssociationsForMap(record: CoreoRecord): Promise<CoreoRecord[]> {
    return this.getRecordAssociations(record);
  }


  public async getImmediateParent(recordId: number): Promise<CoreoRecord> {
    const query = SQLDatabase.select()
      .from('associations')
      .join('attributes', 'attribute', 'attribute.id = associations.attributeId')
      .field('sourceId')
      .where('attribute.questionType = ?', 'child')
      .where('targetId = ?', recordId)
      .limit(1);

    const result = await SQLDatabase.instance.query<any>(query);
    if (result.length === 0) {
      return null;
    }
    const parentId = result[0].sourceId;
    const r = await this.search(q => q.where('id = ?', parentId)).toArray();
    return r[0] || null;
  }

  public async getParent(recordId: number): Promise<CoreoRecord> {
    let p = await this.getImmediateParent(recordId);
    while (p) {
      let t = await this.getImmediateParent(p.id);
      if (!t) {
        break;
      }
      p = t;
    }
    return p;
  }

  private deserializeFeature(serialisedRecord: AppDatabaseRecord): Feature {
    const { id, geometry, geometryCenterLat, geometryCenterLng, ...properties } = serialisedRecord
    return {
      type: 'Feature',
      geometry: JSON.parse(geometry),
      id,
      properties: {
        id,
        center: {
          type: 'Point',
          coordinates: [geometryCenterLng, geometryCenterLat]
        },
        ...properties
      }
    };
  }

  async findProjectMapFeatures(queryBuilder: SQLQueryBuilder): Promise<Feature[]> {

    const query = queryBuilder(SQLDatabase.select().from(this.tableName)
      .field(`${this.tableName}.id`)
      .field(`${this.tableName}.id`, CURSOR_RESULT_KEY)
      .field(`${this.tableName}.geometry`)
      .field(`${this.tableName}.geometryCenterLng`)
      .field(`${this.tableName}.geometryCenterLat`)
      .field(`${this.tableName}.syncState`)
      .field(`${this.tableName}.surveyId`)
      .where(`${this.tableName}.geometry IS NOT NULL`)
    );

    try {
      const features = [];
      // Cursor-based pagination so each batch is a fast index range scan (no OFFSET).
      const cursorQuery = SQLDatabase.instance.queryCursor(query, {
        batchSize: 1000,
        cursorColumn: `${this.tableName}.id`,
        cursorResultKey: CURSOR_RESULT_KEY,
        order: 'desc'
      });
      for await (const batch of cursorQuery) {
        features.push(...batch.map(mf => this.deserializeFeature(mf as any)));
      }
      return features;
    } catch (e) {
      console.warn(e);
      return [];
    }
  }

  public async getRecordsBySyncState(projectId: number, syncState: CoreoRecordSyncStatus): Promise<CoreoRecord[]> {
    return this.search(query => query
      .where('syncState = ?', syncState)
      .where('projectId = ?', projectId)).toArray();
  }

  public async getDraftRecords(projectId: number): Promise<CoreoRecord[]> {
    return this.getRecordsBySyncState(projectId, CoreoRecordSyncStatus.DRAFT);
  }

  public async getPendingUpdates(projectId: number): Promise<CoreoRecord[]> {
    return this.getRecordsBySyncState(projectId, CoreoRecordSyncStatus.PENDING_UPDATE);
  }

  /**
   * Root parent-form records with a direct validation syncError or treeSyncError (validation issue on a descendant).
   */
  public async getParentRecordsWithValidationIssues(
    projectId: number,
    parentSurveyIds: number[]
  ): Promise<CoreoRecord[]> {
    if (parentSurveyIds.length === 0) {
      return [];
    }

    const rootOnlyClause = rootRecordOnlyNotExistsClause('records');

    return this.search(query => query
      .where('projectId = ?', projectId)
      .where('surveyId IN ?', parentSurveyIds)
      .where(recordHasValidationIssueCondition())
      .where(rootOnlyClause)
    ).toArray();
  }

  /**
   * Count of root parent records with validation issues, grouped by project (for menu badges).
   */
  public async getValidationIssueCountsByProject(): Promise<Map<number, number>> {
    const rootOnlyClause = rootRecordOnlyNotExistsClause('r');

    const rows = await SQLDatabase.instance.query<{ projectId: number; errorCount: number }>(
      `SELECT r.projectId, COUNT(*) AS errorCount
        FROM records r
        INNER JOIN forms f ON f.id = r.surveyId AND f.projectId = r.projectId AND f.visible = 1
        WHERE ${recordHasValidationIssueCondition('r')}
        AND ${rootOnlyClause}
        GROUP BY r.projectId`,
      []
    );

    const counts = new Map<number, number>();
    for (const row of rows) {
      counts.set(row.projectId, row.errorCount);
    }
    return counts;
  }

  /**
   * Draft records plus every record reachable from them via child-attribute associations.
   * Pending descendants must not upload until an ancestor leaves draft (avoids server orphans).
   */
  private async getRecordIdsBlockedUnderDraftRoots(projectId: number): Promise<Set<number>> {
    const draft = CoreoRecordSyncStatus.DRAFT;
    const rows = await SQLDatabase.instance.query<{ id: number }>(
      `WITH RECURSIVE blocked(id) AS (
        SELECT id FROM records WHERE projectId = ? AND syncState = ?
        UNION
        SELECT a.targetId
        FROM associations a
        INNER JOIN attributes attr ON attr.id = a.attributeId AND attr.projectId = a.projectId AND attr.questionType = 'child'
        INNER JOIN blocked b ON a.sourceId = b.id
        WHERE a.projectId = ?
      )
      SELECT id FROM blocked`,
      [projectId, draft, projectId]
    );
    return new Set(rows.map(r => r.id));
  }

  /**
   * Pending-update/delete roots that have any descendant (child-attribute associations)
   * whose syncState is not in READY_SYNC_STATES (aligned with checkAssociatesReady in the app).
   * Such roots must not upload until descendants are fixed or explicitly advanced.
   */
  private async getPendingRootIdsWithNonReadyDescendants(projectId: number): Promise<Set<number>> {
    const readyStates = [...READY_SYNC_STATES];
    const placeholders = readyStates.map(() => "?").join(", ");
    const rows = await SQLDatabase.instance.query<{ root_id: number }>(
      `WITH RECURSIVE pending_roots(id) AS (
        SELECT id FROM records WHERE projectId = ? AND syncState IN (?, ?)
      ),
      descendants(root_id, child_id) AS (
        SELECT pr.id AS root_id, a.targetId AS child_id
        FROM pending_roots pr
        INNER JOIN associations a ON a.sourceId = pr.id AND a.projectId = ?
        INNER JOIN attributes attr ON attr.id = a.attributeId AND attr.projectId = a.projectId AND attr.questionType = 'child'
        UNION ALL
        SELECT d.root_id, a.targetId
        FROM descendants d
        INNER JOIN associations a ON a.sourceId = d.child_id AND a.projectId = ?
        INNER JOIN attributes attr ON attr.id = a.attributeId AND attr.projectId = a.projectId AND attr.questionType = 'child'
      )
      SELECT DISTINCT d.root_id AS root_id
      FROM descendants d
      INNER JOIN records r ON r.id = d.child_id AND r.projectId = ?
      WHERE r.syncState NOT IN (${placeholders})`,
      [
        projectId,
        CoreoRecordSyncStatus.PENDING_UPDATE,
        CoreoRecordSyncStatus.PENDING_DELETE,
        projectId,
        projectId,
        projectId,
        ...readyStates
      ]
    );
    return new Set(rows.map(r => r.root_id));
  }

  /*
   * Disabled while investigating upstream bugs that create child-survey rows without a parent association.
   * Re-enable after root causes are fixed if you still want upload-boundary defence in depth.
   *
  private async getRecordIdsBlockedOrphanChildRecords(projectId: number): Promise<Set<number>> {
    const rows = await SQLDatabase.instance.query<{ id: number }>(
      `SELECT r.id FROM records r
      WHERE r.projectId = ?
        AND r.syncState IN (?, ?)
        AND EXISTS (
          SELECT 1 FROM attributes ca
          WHERE ca.projectId = r.projectId
            AND ca.questionType = 'child'
            AND ca.associatedSurveyId = r.surveyId
        )
        AND NOT EXISTS (
          SELECT 1 FROM associations a
          INNER JOIN attributes attr ON attr.id = a.attributeId AND attr.projectId = a.projectId
          WHERE a.projectId = r.projectId
            AND a.targetId = r.id
            AND attr.questionType = 'child'
            AND attr.associatedSurveyId = r.surveyId
        )`,
      [
        projectId,
        CoreoRecordSyncStatus.PENDING_UPDATE,
        CoreoRecordSyncStatus.PENDING_DELETE
      ]
    );
    return new Set(rows.map(r => r.id));
  }
  */

  public async getPendingUpdateIds(projectId: number, surveyIds: number[] = []): Promise<number[]> {
    const pendingQuery = this.selectQuery()
      .field('id')
      .where('syncState IN ?', [CoreoRecordSyncStatus.PENDING_UPDATE, CoreoRecordSyncStatus.PENDING_DELETE])
      .where('projectId = ?', projectId)
      .order('id', true);

    if (surveyIds.length > 0) {
      pendingQuery.where('surveyId IN ?', surveyIds);
    }

    const ids: number[] = [];

    const query = await SQLDatabase.instance.queryAll(pendingQuery);
    for await (const records of query) {
      ids.push(...records.map(r => r.id));
    }

    if (ids.length === 0) {
      return ids;
    }

    const blockedDraft = await this.getRecordIdsBlockedUnderDraftRoots(projectId);
    const blockedNonReadyTree = await this.getPendingRootIdsWithNonReadyDescendants(projectId);
    return ids.filter(
      id => !blockedDraft.has(id) && !blockedNonReadyTree.has(id)
    );
  }

  public async updateSyncState(record: CoreoRecord, syncState: CoreoRecordSyncStatus): Promise<void> {
    const updateSet: capSQLiteSet[] = [];
    const update = this.updateQuery().where('id = ?', record.id).set('syncState', syncState);
    updateSet.push({
      statement: update.toString(),
      values: []
    });
    await SQLDatabase.instance.executeSet(updateSet);
    AppDatabase.instance.events.emit('syncDidUpdate', record.projectId, true, false);
  }

}
